import { createContext, FC, ReactNode, useCallback, useContext, useMemo, useState } from "react";

export const SWEEPSTAKES_HOW_TO_ENTER_ID = "sweepstakes-rules-how-to-enter";

interface SweepstakesRulesContextValue {
  expanded: boolean;
  setExpanded: (expanded: boolean) => void;
  showHowToEnter: () => void;
}

const SweepstakesRulesContext = createContext<SweepstakesRulesContextValue | null>(null);

export const SweepstakesRulesProvider: FC<{ children: ReactNode; onRulesExpanded?: () => void }> = ({
  children,
  onRulesExpanded,
}) => {
  const [expanded, setExpandedState] = useState(false);

  const setExpanded = useCallback(
    (nextExpanded: boolean) => {
      setExpandedState(nextExpanded);
      if (nextExpanded) {
        onRulesExpanded?.();
      }
    },
    [onRulesExpanded]
  );

  const showHowToEnter = useCallback(() => {
    setExpanded(true);
    requestAnimationFrame(() => {
      document.getElementById(SWEEPSTAKES_HOW_TO_ENTER_ID)?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [setExpanded]);

  const value = useMemo(() => ({ expanded, setExpanded, showHowToEnter }), [expanded, setExpanded, showHowToEnter]);

  return <SweepstakesRulesContext.Provider value={value}>{children}</SweepstakesRulesContext.Provider>;
};

export const useSweepstakesRulesContext = (): SweepstakesRulesContextValue => {
  const context = useContext(SweepstakesRulesContext);
  if (!context) {
    throw new Error("useSweepstakesRulesContext must be used within SweepstakesRulesProvider");
  }
  return context;
};
